import { FC } from 'react';
import { ReportsDescription } from 'components/ReportsDescription';
import { useSelector } from 'react-redux';
import {
	getPaymentMethodsSummary,
	getPaymentServices,
} from 'redux/reducers/paymentMethods/selectors';
import { METHOD_TYPE_LABELS } from 'redux/reducers/paymentMethods/constants';
import { TPaymentMethodFee, TPaymentServices } from 'services/api/paymentMethods/types';

const DEFAULT_SUPPORT_EMAIL = 'invoice@easproject.com';

const formatPercent = (fraction: number) =>
	`${(Math.round(fraction * 100 * 100) / 100).toLocaleString('en', { maximumFractionDigits: 2 })}%`;

const describeFee = (fee: TPaymentMethodFee) => {
	const parts: string[] = [];
	if (fee.percent > 0) parts.push(formatPercent(fee.percent));
	if (fee.fixedAmount > 0) parts.push(`${fee.fixedAmount.toFixed(2)} EUR`);
	return parts.join(' + ');
};

// Card and wallet fees from the summary, grouped by the same text ("Card: 3% (EAS VAT compliance, GPSR)").
const buildFeesText = (fees: TPaymentMethodFee[], services: TPaymentServices | null): string => {
	const serviceName = (fee: TPaymentMethodFee) =>
		services?.find((service) => service.id === fee.serviceId)?.name ?? fee.serviceCode;
	const groups = new Map<string, Set<string>>();
	fees
		.filter((fee) => fee.methodType !== 'bacs_direct_debit')
		.forEach((fee) => {
			const text = describeFee(fee);
			if (!text) return;
			const key = `${METHOD_TYPE_LABELS[fee.methodType] ?? fee.methodType}: ${text}`;
			if (!groups.has(key)) groups.set(key, new Set());
			groups.get(key)?.add(serviceName(fee));
		});
	if (groups.size === 0) return '';
	const allServices = new Set(fees.map((fee) => serviceName(fee)));
	return Array.from(groups.entries())
		.map(([key, codes]) =>
			codes.size === allServices.size ? key : `${key} (${Array.from(codes).join(', ')})`,
		)
		.join('; ');
};

export const PaymentMethodsInfoText: FC = () => {
	const summary = useSelector(getPaymentMethodsSummary);
	const services = useSelector(getPaymentServices);
	const supportEmail = summary?.supportEmail || DEFAULT_SUPPORT_EMAIL;
	const feesText = summary ? buildFeesText(summary.fees ?? [], services) : '';

	return (
		<ReportsDescription
			description={
				<>
					Saved payment methods are charged automatically for your EAS invoices for the services you
					select. If several methods cover a service, Direct Debit is used first (UK clients), then
					the default card.
					{feesText && <> A bank fee applies to card and wallet payments: {feesText}.</>}
					<br />
					Questions:{' '}
					<a href={`mailto:${supportEmail}`} style={{ color: 'inherit' }}>
						{supportEmail}
					</a>
					.
				</>
			}
		/>
	);
};
